import { makeStyles } from "@material-ui/core"

import { colors } from "@/styles/colors"

const useStyles = makeStyles({
	paper: {
		width: 860,
		maxWidth: "100%",
		borderRadius: 16,
		boxShadow: "0 1px 2px rgba(20,22,28,.06), 0 24px 60px rgba(20,22,28,.16)",
		border: "1px solid rgba(20,22,28,.04)"
	},

	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "30px 36px 0"
	},

	title: {
		fontSize: 25,
		fontWeight: 600,
		letterSpacing: -0.4,
		lineHeight: 1.1,
		color: colors.gray[900]
	},

	closeButton: {
		width: 48,
		height: 48,
		borderRadius: 999,
		color: colors.gray[500],
		padding: 0,
		"&:hover": {
			backgroundColor: colors.gray[100]
		}
	},

	body: {
		padding: "22px 36px 30px",
		display: "flex",
		flexDirection: "column",
		gap: 24,
		overflowY: "auto"
	},

	searchInputRoot: {
		height: 58,
		fontSize: 17,
		borderRadius: 14,
		backgroundColor: colors.gray[0]
	},

	searchInputOutline: {
		borderWidth: "1.5px",
		borderColor: colors.gray[300],
		borderRadius: 14,
		"&:focus-within": {
			borderColor: colors.purple[500]
		}
	},

	searchInputInner: {
		fontSize: 17,
		padding: "0 14px",
		"&::placeholder": {
			color: colors.gray[400],
			opacity: 1
		}
	},

	searchIcon: {
		fontSize: 20,
		color: colors.gray[400]
	},

	heroCard: {
		borderRadius: 16,
		background: `linear-gradient(122deg, ${colors.purple[400]} 0%, ${colors.purple[500]} 50%, ${colors.purple[700]} 100%)`,
		border: "1px solid rgba(67,50,174,.4)",
		padding: "22px 24px",
		display: "flex",
		alignItems: "center",
		gap: 20,
		cursor: "pointer",
		boxShadow: "0 1px 2px rgba(20,22,28,.06), 0 10px 26px rgba(91,75,218,.22)",
		transition: "box-shadow 0.18s ease, transform 0.18s ease",
		outline: "none",
		"&:hover": {
			boxShadow: "0 4px 6px rgba(20,22,28,.08), 0 14px 32px rgba(91,75,218,.30)",
			transform: "translateY(-2px)"
		},
		"&:focus-visible": {
			outline: "2px solid rgba(255,255,255,.5)"
		}
	},

	heroIcon: {
		width: 46,
		height: 46,
		flexShrink: 0,
		borderRadius: 13,
		background: "rgba(255,255,255,.92)",
		boxShadow: "0 1px 5px rgba(40,30,90,.22)",
		display: "flex",
		alignItems: "center",
		justifyContent: "center"
	},

	heroText: {
		display: "flex",
		flexDirection: "column",
		gap: 4,
		flex: 1,
		minWidth: 0
	},

	heroTitle: {
		fontSize: 18,
		fontWeight: 600,
		letterSpacing: -0.3,
		color: colors.gray[0],
		lineHeight: 1.2
	},

	heroSubtitle: {
		fontSize: 13.5,
		lineHeight: 1.45,
		color: "rgba(255,255,255,.85)",
		maxWidth: "50ch"
	},

	heroFooter: {
		marginLeft: "auto",
		display: "flex",
		alignItems: "center",
		flexShrink: 0
	},

	darkPill: {
		height: 40,
		padding: "0 18px",
		border: "none",
		borderRadius: 999,
		background: colors.gray[0],
		color: colors.gray[800],
		fontSize: 13.5,
		fontWeight: 600,
		cursor: "pointer",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 7,
		whiteSpace: "nowrap",
		fontFamily: "inherit",
		textTransform: "none",
		minWidth: "auto",
		lineHeight: "normal",
		transition: "background 0.15s",
		"& .MuiButton-label": {
			gap: 7,
			display: "inline-flex",
			alignItems: "center"
		},
		"&:hover": {
			background: colors.gray[100]
		},
		"&:disabled": {
			opacity: 0.6,
			cursor: "not-allowed"
		}
	},

	cardUseTemplateButton: {
		background: colors.getPrimaryColor(),
		color: colors.gray[0],
		"&:hover": {
			background: colors.purple[600]
		}
	},

	sectionLabel: {
		display: "flex",
		alignItems: "baseline",
		gap: 10,
		marginBottom: 14
	},

	sectionTitle: {
		fontSize: 15,
		fontWeight: 600,
		letterSpacing: 0.2,
		color: colors.gray[900]
	},

	sectionCount: {
		fontSize: 13,
		color: colors.gray[400],
		fontVariantNumeric: "tabular-nums"
	},

	templateGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(3, 1fr)",
		gap: 16
	},

	libCard: {
		background: colors.gray[0],
		border: `1px solid ${colors.gray[200]}`,
		borderRadius: 16,
		padding: "20px 20px 18px",
		display: "flex",
		flexDirection: "column",
		gap: 10,
		boxShadow: "0 1px 2px rgba(20,22,28,.04), 0 1px 10px rgba(20,22,28,.04)",
		cursor: "pointer",
		minHeight: 160,
		transition: "box-shadow 0.16s ease, transform 0.16s ease, border-color 0.16s ease",
		outline: "none",
		maxHeight: 300,
		"&:hover": {
			borderColor: colors.purple[500],
			boxShadow: "0 10px 28px rgba(91,75,218,.16)",
			transform: "translateY(-3px)"
		},
		"&:focus-visible": {
			borderColor: colors.purple[500],
			boxShadow: `0 0 0 2px ${colors.purple[500]}`
		}
	},

	libName: {
		margin: 0,
		fontSize: 17,
		fontWeight: 600,
		letterSpacing: -0.3,
		color: colors.gray[900],
		lineHeight: 1.3,
		maxWidth: "100%",
		overflow: "hidden",
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		WebkitLineClamp: 3
	},

	libDesc: {
		margin: 0,
		fontSize: 13,
		lineHeight: 1.5,
		color: colors.gray[500],
		flex: 1
	},

	libFooter: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		marginTop: "auto",
		paddingTop: 12
	},

	ghostCircleButton: {
		width: 40,
		height: 40,
		flexShrink: 0,
		borderRadius: 999,
		border: `1px solid ${colors.gray[300]}`,
		background: colors.gray[0],
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		cursor: "pointer",
		transition: "background 0.15s",
		padding: 0,
		"&:hover": {
			background: colors.gray[100]
		},
		"&:disabled": {
			opacity: 0.5,
			cursor: "not-allowed"
		},
		"& svg": {
			pointerEvents: "none"
		}
	},

	loadingContainer: {
		padding: "32px 0"
	},

	emptyText: {
		color: colors.gray[400],
		fontSize: 14,
		textAlign: "center",
		padding: "24px 0"
	}
})

export default useStyles
